import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Keyboard, CameraOff, Loader2, Flashlight } from 'lucide-react';
import { claimPass } from '../lib/pass';
import { requireAuth } from './AuthGate';
import { centerToast } from './CenterToast';
import { platform } from '../lib/platform';

// Decoding uses the browser's own BarcodeDetector where it exists (Chrome and
// Android WebView). Safari has none, and at an awards ceremony a large share of
// the phones are iPhones, so those lazy-load jsQR instead — it is ~45 kB and is
// pulled in only on the devices that actually need it.
//
// Both decoders hand the same string to handleToken, which stays the single
// integration point with the backend.

const DECODE_INTERVAL_MS = 150; // ~7 frames a second; enough to feel instant
const MAX_EDGE = 640;           // downscale before jsQR — full-res frames are wasted work

async function makeDecoder() {
  if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
    try {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      return async (video) => {
        const found = await detector.detect(video);
        return found?.length ? found[0].rawValue : null;
      };
    } catch {
      /* constructor can throw when qr_code is unsupported — fall through to jsQR */
    }
  }

  const { default: jsQR } = await import('jsqr');
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  return async (video) => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;
    const scale = Math.min(1, MAX_EDGE / Math.max(vw, vh));
    const w = Math.round(vw * scale);
    const h = Math.round(vh * scale);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.drawImage(video, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    // dontInvert: door codes are dark-on-light, and skipping the inverted pass
    // roughly halves the work per frame.
    const result = jsQR(data, w, h, { inversionAttempts: 'dontInvert' });
    return result?.data || null;
  };
}

const ERROR_COPY = {
  not_found: 'That code is not recognised.',
  already_claimed: 'This one is already in your passes.',
  not_signed_in: 'Please sign in first.',
  network_error: 'Could not reach the server. Try again.'
};

const DENIED_RECOVERY = {
  'ios-safari': 'Tap the “aA” in the address bar → Website Settings → Camera → Allow, then reopen Scan.',
  'android-chromium': 'Tap the lock icon in the address bar → Permissions → Camera → Allow, then reopen Scan.',
  'desktop-chromium': 'Click the camera icon in the address bar and allow access, then reopen Scan.',
  'in-app-browser': 'This in-app browser blocks the camera. Open IhsanOne in Safari or Chrome, or type the code in below.',
  other: 'Allow camera access for this site in your browser settings, then reopen Scan.'
};

/**
 * ScanSheet — the camera behind the QR button.
 *
 * A member scans to check in at a class or centre and to redeem a reward. The
 * decoded value is handed to `claimPass`, which is the one endpoint that already
 * accepts a scanned token. Confirming which codes map to attendance versus
 * redemption is Azhar's piece of work; `handleToken` below is the single place
 * that needs to change when that is settled.
 */
export default function ScanSheet({ isOpen, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(0);
  const busyRef = useRef(false);
  const decodingRef = useRef(false);

  // starting | scanning | denied | no-camera | working
  const [phase, setPhase] = useState('starting');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  // Bumped to re-acquire the camera after the tab was backgrounded.
  const [restartKey, setRestartKey] = useState(0);

  const stopCamera = useCallback(() => {
    window.clearInterval(loopRef.current);
    loopRef.current = 0;
    decodingRef.current = false;
    const stream = streamRef.current;
    if (stream) stream.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setTorchOn(false);
    setTorchAvailable(false);
  }, []);

  const finish = useCallback(() => {
    stopCamera();
    setManualOpen(false);
    setManualValue('');
    onClose?.();
  }, [onClose, stopCamera]);

  const handleToken = useCallback(async (raw) => {
    const token = (raw || '').trim();
    if (!token || busyRef.current) return;
    busyRef.current = true;
    window.clearInterval(loopRef.current);
    setPhase('working');
    // A short tick confirms the read before the network call — at a busy door
    // the member looks up from the phone as soon as they feel it.
    try { navigator.vibrate?.(30); } catch { /* unsupported */ }

    const signedIn = await requireAuth();
    if (!signedIn) {
      busyRef.current = false;
      finish();
      return;
    }

    const result = await claimPass(token);
    busyRef.current = false;
    if (result?.ok) {
      centerToast(result.program?.name ? `Checked in — ${result.program.name}` : 'Scan accepted', '✅');
    } else {
      centerToast(ERROR_COPY[result?.error] || 'Could not use that code.', '⚠️');
    }
    finish();
  }, [finish]);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      setTorchAvailable(false);
    }
  }, [torchOn]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    busyRef.current = false;
    setPhase('starting');

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) setPhase('no-camera');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // iOS rejects play() outside a gesture unless the video is muted and
          // inline; both are set on the element.
          await videoRef.current.play().catch(() => {});
        }

        const track = stream.getVideoTracks()[0];
        if (track?.getCapabilities) {
          try { setTorchAvailable(Boolean(track.getCapabilities().torch)); } catch { /* unsupported */ }
        }

        const decode = await makeDecoder();
        if (cancelled) return;
        setPhase('scanning');

        loopRef.current = window.setInterval(async () => {
          const video = videoRef.current;
          if (!video || video.readyState < 2 || busyRef.current || decodingRef.current) return;
          decodingRef.current = true;
          try {
            const value = await decode(video);
            if (value) handleToken(value);
          } catch {
            /* a frame that cannot be read is not worth reporting */
          } finally {
            decodingRef.current = false;
          }
        }, DECODE_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setPhase(err?.name === 'NotFoundError' ? 'no-camera' : 'denied');
        setManualOpen(true);
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [isOpen, restartKey, handleToken, stopCamera]);

  // Backgrounding the tab must release the camera — otherwise the indicator
  // light stays on. Re-acquire when the member comes back.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopCamera();
      } else if (!busyRef.current) {
        setRestartKey((key) => key + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isOpen, stopCamera]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const statusLine = {
    starting: 'Starting the camera…',
    scanning: 'Point the camera at the QR code',
    working: 'Checking your code…',
    denied: 'Camera access was blocked',
    'no-camera': 'No camera found on this device'
  }[phase];

  const cameraFailed = phase === 'denied' || phase === 'no-camera';

  return (
    <>
    <div aria-hidden="true" className="fixed inset-0 z-[74] bg-black" />
    <div
      className="fixed inset-0 mx-auto max-w-[480px] z-[75] bg-black flex flex-col overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Scan a QR code"
      data-testid="scan-sheet"
    >
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className={`absolute inset-0 h-full w-full object-cover ${cameraFailed ? 'hidden' : ''}`}
      />

      {/* Cut-out framing square, dimmed outside so the target area reads clearly */}
      {!cameraFailed && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative h-60 w-60 rounded-3xl" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)' }}>
            <span className="absolute -top-px -left-px h-10 w-10 border-t-4 border-l-4 border-[#5EEAD4] rounded-tl-3xl" />
            <span className="absolute -top-px -right-px h-10 w-10 border-t-4 border-r-4 border-[#5EEAD4] rounded-tr-3xl" />
            <span className="absolute -bottom-px -left-px h-10 w-10 border-b-4 border-l-4 border-[#5EEAD4] rounded-bl-3xl" />
            <span className="absolute -bottom-px -right-px h-10 w-10 border-b-4 border-r-4 border-[#5EEAD4] rounded-br-3xl" />
          </div>
        </div>
      )}

      <div className="relative z-10 flex items-start justify-between p-4">
        <button
          type="button"
          data-testid="scan-close"
          onClick={finish}
          aria-label="Close scanner"
          className="h-10 w-10 rounded-full bg-black/50 text-white flex items-center justify-center backdrop-blur-sm"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          {torchAvailable && !cameraFailed && (
            <button
              type="button"
              data-testid="scan-torch"
              onClick={toggleTorch}
              aria-pressed={torchOn}
              aria-label={torchOn ? 'Turn off the light' : 'Turn on the light'}
              className={`h-10 w-10 rounded-full flex items-center justify-center backdrop-blur-sm ${
                torchOn ? 'bg-[#5EEAD4] text-teal-900' : 'bg-black/50 text-white'
              }`}
            >
              <Flashlight className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            data-testid="scan-manual-toggle"
            onClick={() => setManualOpen((open) => !open)}
            className="h-10 px-3.5 rounded-full bg-black/50 text-white text-xs font-semibold flex items-center gap-1.5 backdrop-blur-sm"
          >
            <Keyboard className="h-4 w-4" />
            Enter code
          </button>
        </div>
      </div>

      {cameraFailed && (
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 text-center">
          <CameraOff className="h-10 w-10 text-white/50 mb-3" />
          <p className="text-sm font-semibold text-white">{statusLine}</p>
          <p className="text-xs text-white/60 mt-1.5 max-w-[280px] leading-relaxed">
            {phase === 'denied'
              ? DENIED_RECOVERY[platform()] || DENIED_RECOVERY.other
              : 'You can still type the code in by hand.'}
          </p>
        </div>
      )}

      <div className="relative z-10 mt-auto p-5 pb-8">
        {!cameraFailed && (
          <p className="text-center text-sm font-medium text-white mb-4 flex items-center justify-center gap-2">
            {phase === 'working' && <Loader2 className="h-4 w-4 animate-spin" />}
            {statusLine}
          </p>
        )}

        {manualOpen && (
          <form
            data-testid="scan-manual-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleToken(manualValue);
            }}
            className="flex gap-2 max-w-sm mx-auto"
          >
            <input
              type="text"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              placeholder="Type or paste the code"
              autoComplete="off"
              data-testid="scan-manual-input"
              className="flex-1 h-11 rounded-full bg-white px-4 text-sm text-[#0F172A] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#5EEAD4]"
            />
            <button
              type="submit"
              disabled={!manualValue.trim() || phase === 'working'}
              data-testid="scan-manual-submit"
              className="h-11 px-5 rounded-full bg-[#5EEAD4] text-teal-900 text-sm font-bold disabled:opacity-40"
            >
              Go
            </button>
          </form>
        )}
      </div>
    </div>
    </>
  );
}
